/* eslint-disable react/forbid-elements */
import React, { FC, useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';
import useTranslator from 'Client/hooks/useTranslator';
import { forDesktop, forMidDesktop } from '@westwing/ui-kit/breakPoints';
import useConfig from 'AppShell/hooks/useConfig';
import useOwwFooterTracking, {
    FOOTER_TRACKING_EVENT_CATEGORY,
} from 'OneWestwingFooter/providers/OwwFooterTrackingProvider';
import { useCurrentPageType } from 'Client/hooks/UrlManager/pageTypeHooks';
import { body2Bold, body2 } from '@westwing/ui-kit/typography';
import ReturnPurchaseIcon from '@westwing/ui-kit/Icons/Svgs/ReturnPurchaseIcon';
import { FooterCountrySelector } from 'OneWestwingFooter/FooterCountrySelector';
import BellaHybridLink from 'Client/components/Generic/Links/BellaHybridLink';
import ClubHybridLink from 'Club/components/Links/ClubHybridLink';
import useRouter from 'AppShell/hooks/useRouter';
import { useAppBridge } from 'AppShell/appBridge/useAppBridge';
import { trimTrailingSlash } from 'Client/urlService';
import { OneWestwingFooterIds } from 'Common/qaIds';

const LegalSectionWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    max-width: 1440px;
    margin: auto;
    flex-direction: column;
    justify-content: center;
    padding: 12px 20px;
    ${forDesktop`
        padding: 16px 20px;
        justify-content: normal;
        height: 52px;
        gap: 32px;
        flex-direction: row;
    `}
    ${forMidDesktop`
        padding: 16px 40px;
    `}
`;

const LegalSectionWrapperContainer = styled.div`
    background: ${({ theme }) => theme.colors.charcoal100};
    ${forDesktop`
        padding: 0px 40px;
    `}
`;

const LegalEntity = styled.div`
    ${body2Bold}
    color: ${({ theme }) => theme.colors.white};
    order: 2;
    ${forDesktop`
        order: initial;
    `}
`;
const CountrySelectorWrapper = styled.div<{ $isHidden: boolean }>`
    display: none;
    ${({ $isHidden }) => css`
        ${forDesktop`
            display: ${$isHidden ? 'none' : 'block'};
        `}
    `}
`;

const LegalSectionLinks = styled.ul`
    display: flex;
    gap: 24px;
    padding-inline-start: 0;
    column-gap: 24px;
    row-gap: 2px;
    flex-wrap: wrap;
    justify-content: center;

    ${forDesktop`
        flex-grow: 1;
        justify-content: flex-start;
        flex-wrap: nowrap;
    `}
`;
const LegalSectionLink = styled.li`
    position: relative;
    &:not(:first-child) {
        &:before {
            content: '';
            display: inline-block;
            width: 1px;
            top: 4px;
            height: 12px;
            background: ${({ theme }) => theme.colors.charcoal30};
            left: -12px;
            position: absolute;
        }
    }
`;

const ReturnInfoBlock = styled.div`
    display: none;
    ${forDesktop`
        display: flex;
        gap: 16px;
        ${body2}
        color: ${({ theme }) => theme.colors.white};
    `}
`;
const SeoLinks = styled.div`
    display: none;
`;

const StyledBellaLink = styled(BellaHybridLink)`
    ${body2}
    white-space: nowrap;
    color: ${({ theme }) => theme.colors.charcoal30};
`;

const StyledClubLink = styled(ClubHybridLink)`
    ${body2}
    white-space: nowrap;
    color: ${({ theme }) => theme.colors.charcoal30};
`;

export const FooterLegalSection: FC<{ isClub?: boolean; isMiniMode?: boolean }> = ({
    isClub = false,
    isMiniMode = false,
}) => {
    const t = useTranslator();
    const config = useConfig();
    const legalYearStart = useMemo(() => (config.currentCountry === 'es' ? '2012' : '2011'), [config.currentCountry]);

    const { dataProtection, agb, imprint, agbB2b } = isClub ? config.links.clubPaths : config.links;

    // We actually want those links (which are display none) to the other platform, so the inverted condition is intentional
    const seoLinks = !isClub ? config.links.clubPaths : config.links;
    const { ga } = useOwwFooterTracking();

    const { pageType } = useCurrentPageType();
    const trackClick = useCallback(
        (actionName: string) => {
            ga.trackNonInteractiveGenericEvent(FOOTER_TRACKING_EVENT_CATEGORY, {
                action: actionName,
                label: pageType,
            });
        },
        [ga, pageType]
    );

    const { isAppWebviewPage } = useRouter();
    const appBridge = useAppBridge('mf-checkout-payment');

    const handleLinkClick = (
        e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
        trackingEvent: string,
        path: string
    ) => {
        if (isAppWebviewPage) {
            e.preventDefault();
            appBridge.sendShowOverlay(`${trimTrailingSlash(config.links.url)}${path}`);
        }
        trackClick(trackingEvent);
    };

    const Link = isClub ? StyledClubLink : StyledBellaLink;
    const currentYear = useMemo(() => new Date().getFullYear(), []);
    return (
        <LegalSectionWrapperContainer>
            <LegalSectionWrapper>
                <LegalEntity>
                    © {legalYearStart} – {currentYear} Westwing
                </LegalEntity>

                <LegalSectionLinks>
                    <LegalSectionLink>
                        <Link onClick={e => handleLinkClick(e, 'Imprint - click', imprint)} to={imprint}>
                            {t('Footer_Imprint')}
                        </Link>
                    </LegalSectionLink>
                    <LegalSectionLink>
                        <Link
                            data-testid={OneWestwingFooterIds.owwLinksAGB}
                            onClick={e => handleLinkClick(e, 'Terms and conditions - click', agb)}
                            to={agb}
                        >
                            {t('Footer_AGB')}
                        </Link>
                    </LegalSectionLink>
                    {agbB2b && (
                        <LegalSectionLink>
                            <Link
                                onClick={e => handleLinkClick(e, 'Terms and conditions B2B - click', agbB2b)}
                                to={agbB2b}
                            >
                                {t('Footer_ABG_B2B')}
                            </Link>
                        </LegalSectionLink>
                    )}
                    <LegalSectionLink>
                        <Link
                            onClick={e => handleLinkClick(e, 'Privacy Policy - click', dataProtection)}
                            to={dataProtection}
                        >
                            {t('Footer_Data_protection')}
                        </Link>
                    </LegalSectionLink>
                    {config.links.cookiePolicy && (
                        <LegalSectionLink>
                            <Link
                                onClick={e => handleLinkClick(e, 'cookie policy - click', config.links.cookiePolicy)}
                                to={config.links.cookiePolicy}
                            >
                                {t('Footer_cookie policy')}
                            </Link>
                        </LegalSectionLink>
                    )}
                </LegalSectionLinks>

                <CountrySelectorWrapper data-testid={OneWestwingFooterIds.owwCountrySelector} $isHidden={isMiniMode}>
                    <FooterCountrySelector testId={OneWestwingFooterIds.owwCountrySelectorOverlay} isClub={isClub} />
                </CountrySelectorWrapper>

                {isMiniMode && (
                    <ReturnInfoBlock>
                        <ReturnPurchaseIcon />
                        <span>{t('Footer_USP_free return')}</span>
                    </ReturnInfoBlock>
                )}

                <SeoLinks>
                    <a data-testid="OOWFooter_legalSection_seoLinks" href={seoLinks.agb}>
                        {t('Footer_AGB')}
                    </a>
                    <a data-testid="OOWFooter_legalSection_seoLinks" href={seoLinks.imprint}>
                        {t('Footer_Imprint')}
                    </a>
                    <a data-testid="OOWFooter_legalSection_seoLinks" href={seoLinks.dataProtection}>
                        {t('Footer_Data_protection')}
                    </a>
                </SeoLinks>
            </LegalSectionWrapper>
        </LegalSectionWrapperContainer>
    );
};
